import { AlertCircle, Info } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Fragment, type ReactNode } from 'react';

import { buildDynamicWidgetDiagnostic } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_diagnostic';
import {
    type DynamicWidgetIssueControl,
    getDynamicWidgetErrorCatalogEntry,
    getDynamicWidgetIssueBody,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_error_catalog';
import {
    captureDynamicWidgetMetric,
    DynamicWidgetMetricReporter,
    type DynamicWidgetMetricTags,
    getDynamicWidgetMetricTags,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import { type DynamicWidgetIssue } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    useDynamicWidgetSupportActions,
    useRenderedDynamicWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_support_context';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Button } from '@apps/web/components/ui/button';
import { EVENTS } from '@apps/web/lib/events';
import { cn } from '@apps/web/lib/utils';

type DynamicWidgetIssueNoticeHandlers = {
    readonly onRetryRead?: () => void;
    readonly onShowSource?: () => void;
    readonly onUseClassicView?: () => void;
    readonly onAskPhoebe?: () => void;
};

type DynamicWidgetIssueNoticeProps = DynamicWidgetIssueNoticeHandlers & {
    readonly issue: DynamicWidgetIssue;
    readonly fieldLabel?: string;
    readonly metricTags?: DynamicWidgetMetricTags;
};

const CONTROL_LABELS: Record<DynamicWidgetIssueControl, string> = {
    retry_read: 'Retry read',
    show_source: 'Show source',
    use_classic_view: 'Use classic view',
    ask_phoebe: 'Ask Phoebe',
};

function getControlHandler(
    control: DynamicWidgetIssueControl,
    handlers: DynamicWidgetIssueNoticeHandlers,
): (() => void) | undefined {
    switch (control) {
        case 'retry_read':
            return handlers.onRetryRead;
        case 'show_source':
            return handlers.onShowSource;
        case 'use_classic_view':
            return handlers.onUseClassicView;
        case 'ask_phoebe':
            return handlers.onAskPhoebe;
        default: {
            const exhaustiveControl: never = control;
            return exhaustiveControl;
        }
    }
}

/**
 * Resolves the two host-backed controls from context, so a widget renders an
 * issue notice without threading support wiring through every call site. The
 * controls stay hidden unless the host actually supplies a handler: "Ask
 * Phoebe" needs a conversation that accepts widget metadata (never an admin
 * trace), and "Show source" needs the debug sidebar.
 */
function useDynamicWidgetIssueSupport(
    issue: DynamicWidgetIssue,
): DynamicWidgetIssueNoticeHandlers {
    const model = useRenderedDynamicWidgetModel();
    const support = useDynamicWidgetSupportActions();
    const posthog = usePostHog();
    if (model === null || support === null) {
        return {};
    }
    const onAskPhoebe = support.onAskPhoebe;
    const onShowSource = support.onShowSource;
    return {
        ...(onAskPhoebe === undefined
            ? {}
            : {
                  onAskPhoebe: () => {
                      void (async () => {
                          const attached = await onAskPhoebe(
                              buildDynamicWidgetDiagnostic({ model, issue }),
                          );
                          if (attached === false) {
                              return;
                          }
                          captureDynamicWidgetMetric(
                              posthog,
                              EVENTS.DYNAMIC_WIDGET_DIAGNOSTIC_ATTACHED,
                              {
                                  ...getDynamicWidgetMetricTags(model),
                                  errorCode: issue.code,
                              },
                          );
                      })();
                  },
              }),
        ...(onShowSource === undefined
            ? {}
            : {
                  onShowSource: () => {
                      onShowSource(model.widgetId, issue.bindingIds[0] ?? null);
                  },
              }),
    };
}

function IssueMetrics({
    issue,
    metricTags,
}: {
    readonly issue: DynamicWidgetIssue;
    readonly metricTags?: DynamicWidgetMetricTags;
}) {
    if (metricTags === undefined) {
        return null;
    }
    const tags = {
        ...metricTags,
        errorCode: issue.code,
    };
    return (
        <Fragment>
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_ISSUE_SHOWN}
                tags={tags}
                dedupeKey={issue.id}
            />
            {issue.code === 'source_missing' ? (
                <DynamicWidgetMetricReporter
                    event={EVENTS.DYNAMIC_WIDGET_BINDING_MISSING}
                    tags={tags}
                    dedupeKey={issue.id}
                />
            ) : null}
        </Fragment>
    );
}

function IssueControls({
    issue,
    handlers,
}: {
    readonly issue: DynamicWidgetIssue;
    readonly handlers: DynamicWidgetIssueNoticeHandlers;
}) {
    const catalog = getDynamicWidgetErrorCatalogEntry(issue.code);
    const controls = catalog.allowedControls.flatMap((control) => {
        const handler = getControlHandler(control, handlers);
        if (
            handler === undefined ||
            (control === 'retry_read' && !issue.retryable)
        ) {
            return [];
        }
        return [{ control, handler }];
    });
    if (controls.length === 0) {
        return null;
    }
    return (
        <div className="mt-1 flex flex-wrap gap-1.5">
            {controls.map(({ control, handler }) => (
                <Button
                    key={control}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handler}
                >
                    {CONTROL_LABELS[control]}
                </Button>
            ))}
        </div>
    );
}

export function DynamicWidgetIssueNotice({
    issue,
    fieldLabel,
    metricTags,
    onRetryRead,
    onShowSource,
    onUseClassicView,
    onAskPhoebe,
}: DynamicWidgetIssueNoticeProps) {
    const catalog = getDynamicWidgetErrorCatalogEntry(issue.code);
    const supportHandlers = useDynamicWidgetIssueSupport(issue);
    const handlers = {
        onRetryRead,
        onShowSource: onShowSource ?? supportHandlers.onShowSource,
        onUseClassicView,
        onAskPhoebe: onAskPhoebe ?? supportHandlers.onAskPhoebe,
    };
    const metrics = <IssueMetrics issue={issue} metricTags={metricTags} />;

    if (issue.scope === 'field') {
        return (
            <Fragment>
                {metrics}
                <p
                    className={cn(
                        'text-[11px]',
                        catalog.severity === 'error'
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                    )}
                    role="status"
                    data-dynamic-widget-issue-scope="field"
                >
                    {fieldLabel ?? catalog.title}
                </p>
            </Fragment>
        );
    }

    if (issue.scope === 'action') {
        return (
            <Fragment>
                {metrics}
                <div
                    className={cn(
                        'max-w-64 text-[11px]',
                        catalog.severity === 'error'
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                    )}
                    role="status"
                    data-dynamic-widget-issue-scope="action"
                >
                    <p className="font-medium">{catalog.title}</p>
                    <p>{getDynamicWidgetIssueBody(issue)}</p>
                    <IssueControls issue={issue} handlers={handlers} />
                </div>
            </Fragment>
        );
    }

    const Icon: typeof AlertCircle =
        catalog.severity === 'info' ? Info : AlertCircle;
    const notice: ReactNode = (
        <Alert
            variant={catalog.severity === 'error' ? 'destructive' : 'default'}
            className="py-2.5"
            data-dynamic-widget-issue-scope={issue.scope}
        >
            <Icon aria-hidden="true" />
            <AlertTitle className="text-xs">{catalog.title}</AlertTitle>
            <AlertDescription className="text-xs">
                <p>{getDynamicWidgetIssueBody(issue)}</p>
                <IssueControls issue={issue} handlers={handlers} />
            </AlertDescription>
        </Alert>
    );
    return (
        <Fragment>
            {metrics}
            {notice}
        </Fragment>
    );
}
